import { tourTarget, type TourStep } from './types';

export interface CustomerTourInput {
  /**
   * The e-file the tour opens to explain a result. An accepted with errors
   * e-file is preferred: it is the only state that shows all three of
   * transmissions, per record errors and a completed timeline.
   */
  filingId: string | null;
  /**
   * An e-file the IRS rejected, so the replacement page has something real to
   * point at. Without one the tour skips that step rather than opening a page
   * that would refuse the customer.
   */
  rejectedFilingId?: string | null;
}

/**
 * The customer tour. It follows the job end to end: select a year, review what
 * has been submitted, submit something new, read the IRS response, then manage
 * keys and users. Each step explains behaviour rather than naming a control.
 */
export function customerTourSteps({ filingId, rejectedFilingId = null }: CustomerTourInput): TourStep[] {
  const filings = { to: '/e-files' };
  const steps: TourStep[] = [
    {
      target: tourTarget('product'),
      title: 'Tab Service',
      content:
        'Tab Service submits information returns to the IRS on your behalf. Records are validated, filed under Tab Service’s transmitter control code, and the IRS response is reported for every record.',
      route: filings,
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: tourTarget('tax-year'),
      title: 'Tax year',
      content:
        'This is the only tax year control in the application. It governs the e-files list, the counts beside it, and the year every new e-file is submitted for. The choice is stored and restored on the next visit.',
      route: filings,
      placement: 'bottom',
    },
    {
      target: tourTarget('mode'),
      title: 'Practice and live modes',
      content:
        'In practice mode all e-files are sent to the IRS test system, so no submission is a real e-file. Only Tab Service can move an account to live, which happens once test e-files return clean.',
      route: filings,
      placement: 'bottom',
    },
    {
      target: tourTarget('nav-e-files'),
      title: 'E-files',
      content:
        'One row per submitted batch, newest first. The list refreshes every 15 seconds while any e-file is queued, transmitting, or awaiting an IRS acknowledgement.',
      route: filings,
      placement: 'right',
    },
    {
      target: tourTarget('status-chips'),
      title: 'Status shortcuts',
      content:
        'These count the e-files in the selected tax year, and only the statuses your year actually contains are shown. Select one to narrow the table to that status, and select it again to clear it.',
      route: filings,
      placement: 'bottom',
    },
    {
      target: tourTarget('e-files-table'),
      title: 'Reading the table',
      content:
        'Form and tax year identify the batch; Sent as records the file format supplied. Records is the number submitted and Accepted is the number the IRS took. Select any row to open the e-file.',
      route: filings,
      placement: 'top',
    },
    {
      target: tourTarget('new-e-file'),
      title: 'Starting an e-file',
      content:
        'Opens the submission wizard. Nothing reaches the IRS until the final step, so the earlier steps can be reviewed and revised freely.',
      route: filings,
      placement: 'bottom',
    },
    {
      target: tourTarget('wizard-steps'),
      title: 'Four steps',
      content:
        'Format, upload, validation, submit. Any step can be revisited, and no data is transmitted until the submit step is confirmed.',
      route: { to: '/e-files/new' },
      placement: 'bottom',
    },
    {
      target: tourTarget('format-cards'),
      title: 'Supported formats',
      content:
        'CSV and Excel carry one row per return, with column names matched automatically. An IRS 1220 file is the FIRE format, accepted exactly as produced. IRIS XML is a complete transmission, validated against the IRS schema.',
      route: { to: '/e-files/new' },
      placement: 'top',
    },
    {
      target: tourTarget('form-year'),
      title: 'Form',
      content:
        'CSV and Excel files do not identify their form, so it is selected here. The tax year is not asked for anywhere in the wizard: the e-file uses the year in the header. A 1220 file or IRIS XML declares its own year, and a file that disagrees with the header is blocked at validation.',
      route: { to: '/e-files/new' },
      placement: 'top',
    },
    {
      target: tourTarget('send-mode'),
      title: 'Paste JSON',
      content:
        'For a small batch, or an integration under development, canonical records can be pasted directly. Up to 1,000 records per submission, validated exactly as an uploaded file is.',
      route: { to: '/e-files/new' },
      placement: 'bottom',
    },
  ];

  if (filingId) {
    const detail = { to: '/e-files/$efileId', params: { efileId: filingId } };
    steps.push(
      {
        target: tourTarget('e-file-summary'),
        title: 'EFile summary',
        content:
          'The facts required to identify an e-file: status, form, tax year, whether it is an original or a correction, and the format submitted. The counts below are the IRS result to date.',
        route: detail,
        placement: 'bottom',
      },
      {
        target: tourTarget('transmissions'),
        title: 'Transmissions',
        content:
          'Large batches are sent to the IRS in chunks, one row per chunk. The receipt id is the identifier the IRS issued for that chunk and should be quoted in any query. Both XML documents are available to download.',
        route: detail,
        placement: 'top',
      },
      {
        target: tourTarget('records'),
        title: 'Records',
        content:
          'One row per return. Selecting a row opens the record, where the issue is described plainly, the IRS wording sits beneath it, and the tag says whether the correction is yours or Tab Service’s. A record accepted with an error is filed but requires a correction.',
        route: detail,
        placement: 'top',
      },
      {
        target: tourTarget('record-fix'),
        title: 'Fixing a record',
        content:
          'Open a record and the data submitted for it can be edited field by field, with taxpayer ids masked until a new one is entered. Saving queues the fix rather than sending it: after a rejection it is queued as a replacement, after an acceptance as a correction. A record needing no change can be closed with a note instead, and this column reports where each one stands.',
        route: detail,
        placement: 'top',
      },
      {
        target: tourTarget('submit-queued'),
        title: 'Submitting queued fixes',
        content:
          'The IRS takes a correction or a replacement as one transmission, so queued fixes are submitted together rather than one at a time. The confirmation lists the records going out and what they will be filed as, and the result is a new e-file that points back at this one.',
        route: detail,
        placement: 'bottom',
      },
      {
        target: tourTarget('timeline'),
        title: 'Activity',
        content:
          'Every step from upload to IRS acknowledgement, oldest first. If an e-file stalls or is rejected, this is where the point of failure is recorded.',
        route: detail,
        placement: 'left',
      },
      {
        target: tourTarget('e-file-actions'),
        title: 'What can be filed against this e-file',
        content:
          'These buttons appear only when the IRS result allows the action. An e-file the IRS accepted can be corrected; an e-file it rejected can be replaced. An e-file still in flight offers neither, because the result is not known yet.',
        route: detail,
        placement: 'bottom',
      },
      {
        target: tourTarget('correction-records'),
        title: 'Submitting corrections',
        content:
          'A correction replaces records the IRS has already accepted. Send the corrected records as a file or as JSON, giving each one the same reference as the accepted record it corrects. This table lists those references, and Tab Service supplies the IRS back-references itself.',
        route: { to: '/e-files/$efileId/correct', params: { efileId: filingId } },
        placement: 'top',
      },
    );
  }

  if (rejectedFilingId) {
    steps.push({
      target: tourTarget('replacement-transmissions'),
      title: 'Replacing a rejected e-file',
      content:
        'When the IRS rejects a transmission, nothing in it was filed. A replacement resends the fixed records against the rejected receipt, inside the IRS grace window. This table names the transmissions to resend and what the IRS said about each one.',
      route: { to: '/e-files/$efileId/replace', params: { efileId: rejectedFilingId } },
      placement: 'top',
    });
  }

  steps.push(
    {
      target: tourTarget('keys'),
      title: 'API keys',
      content:
        'A key authenticates a payroll or ledger system so it can submit without a person signing in. The key is displayed once, at creation, and must be copied then. A compromised key can be revoked here and replaced.',
      route: { to: '/settings' },
      placement: 'top',
    },
    {
      target: tourTarget('users'),
      title: 'Owners and members',
      content:
        'A member can submit e-files and read everything. An owner can do the same and additionally manage users and keys. Invited users receive an email to set their own password.',
      route: { to: '/settings', search: { tab: 'users' } },
      placement: 'top',
    },
    {
      target: tourTarget('help'),
      title: 'End of tour',
      content:
        'This is your account, at the foot of the menu: the tour can be restarted from here at any time, and so can the appearance of the app and the way out. Contact Tab Service if anything covered here needs clarification.',
      route: filings,
      placement: 'right',
    },
  );

  return steps;
}
